<script setup lang="ts">
// A group's form (EditDrawer): destinations started and stopped together.
import { reactive } from 'vue'
import type { EditTarget, Group } from '../../api'
import { newId, slug } from '../../util'
import { clone, useEditContext } from './context'
import ScheduleEditor from './ScheduleEditor.vue'

const props = defineProps<{ target: EditTarget }>()
const emit = defineEmits<{ close: [] }>()
const t = props.target
const { node, cfg, isNew } = useEditContext(t)
const group = reactive<Group>(
    t.kind === 'group'
        ? clone(cfg.value.groups?.find((g) => g.id === t.id) ?? { id: '', name: '', destinations: [] })
        : { id: '', name: '', destinations: [] },
)
function toggleMember(id: string) {
    const i = group.destinations.indexOf(id)
    if (i >= 0) group.destinations.splice(i, 1)
    else group.destinations.push(id)
}
async function save() {
    const id = group.id || newId(slug(group.name) + '-', new Set((cfg.value.groups ?? []).map((g) => g.id)))
    await node.save('groups', id, { ...group, id, schedule: group.schedule?.length ? group.schedule : undefined })
}
defineExpose({ save, snapshot: () => JSON.stringify(group) })
</script>

<template>
    <div class="mb-2.5"><label>Name</label><input v-model="group.name" required autofocus placeholder="Go live" /></div>
    <label>Destinations</label>
    <div v-for="e in cfg.encodes" :key="e.id">
        <div class="mt-1.5 mb-0.5 text-xs text-fg-3">{{ e.name }}</div>
        <label
            v-for="d in cfg.destinations.filter((d) => d.encode_id === e.id)"
            :key="d.id"
            class="flex items-center gap-1.5 text-fg"
        >
            <input
                type="checkbox"
                class="w-auto"
                :checked="group.destinations.includes(d.id)"
                @change="toggleMember(d.id)"
            />
            {{ d.name }}
            <span class="text-fg-2">· {{ d.kind === 'file' ? 'recording' : d.kind.replace('_', ' ') }}</span>
        </label>
    </div>
    <div class="mt-3"><ScheduleEditor v-model="group.schedule" what="its destinations" /></div>
    <div v-if="!isNew" class="mt-3">
        <button
            type="button"
            class="btn-danger"
            @click="
                node.remove('groups', group.id).then(
                    () => emit('close'),
                    () => {},
                )
            "
        >
            Delete group
        </button>
    </div>
</template>
